import lodash from "lodash";
import { Tree, TreeNode } from "@/types/tree";
import { Control } from "@/types/controls";
import { configStore, documentStore } from "@/store/store";
import {
  FieldToComplete,
  CompletedField,
  PromptInfo,
  UnsplashImage,
  FieldToFill,
  OpenAIErrorV
} from "@/lib/ai/types";
import { numberToOrdinal } from "@/lib/ai/prompts";
import fpJsonParse from "@/util/fp-json-parse";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { findNode, getNodeIndex, getOutermostAncestor } from "../tree/tree";
import { SECTION_SLUG } from "@/lib/elements/constants";
import defensiveCopy from "@/util/defensive-copy";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";
import NotificationLogger from "@/lib/notifications/notifications";
import { ELEMENTS_TO_IGNORE } from "@/futurelayer/ai";
import { isFutureLayerMode } from "@/futurelayer/ui/ui";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { breakdanceUndo } from "@/lib/undo/undo";

const emptyCompletion = {
  elements: [],
  unsplash_keywords: [],
  sectionType: "Empty Completion"
};

export function getDefaultAiSettings() {
  return {
    isApiKeySet: false,
    isStreamingEnabled: false,
    model: "",
    locale: "en_US",
    prompt: {
      businessName: "",
      businessDescription: "",
      qualifiers: "",
      presets: []
    }
  };
}

export function processCompletion(
  maybeJsonCompletion: string | undefined,
  fieldsToComplete: FieldToComplete[]
): {
  elements: CompletedField[];
  unsplash_keywords: string[];
  sectionType: string;
} {
  if (!maybeJsonCompletion) {
    return emptyCompletion;
  }

  const output = pipe(
    fpJsonParse(maybeJsonCompletion),
    fold(
      errors => {
        console.log("Error parsing JSON from AI:", errors[0].msg);
        console.log(maybeJsonCompletion);
        console.log("");
        return null;
      },
      completion => completion
    )
  ) as {
    elements: FieldToFill[];
    unsplash_keywords: string[];
    sectionType: string;
  };

  if (!output) {
    return emptyCompletion;
  }

  if (!Array.isArray(output?.elements)) {
    return emptyCompletion;
  }

  return {
    ...output,
    elements: output.elements
      .filter(e => {
        // Remove elements that don't have an index and content set.
        // This usually happens when the AI hasn't finished generating the completion.
        return e.index !== undefined && e.content !== undefined;
      })
      .map(g => ({
        nodeId: fieldsToComplete[g.index].nodeId,
        controlPath: fieldsToComplete[g.index].controlPath,
        value: g.content
      }))
  };
}

let lastFields: CompletedField[] = [];

function isSameField(a: CompletedField, b: CompletedField) {
  return (
    a.nodeId === b.nodeId &&
    a.controlPath === b.controlPath &&
    a.value === b.value
  );
}

export function updateDocument(fields: CompletedField[]) {
  const newFields = fields.filter(
    f => !lastFields.some(lf => isSameField(lf, f))
  );

  lastFields = fields;

  breakdanceUndo.transaction(() => {
    newFields.forEach(v => {
      documentStore.throttledPropertyChanged({
        elementId: v.nodeId,
        path: v.controlPath,
        value: v.value,
        meta: {
          snapshotLabel: "Update from Generative AI"
        }
      });
    });
  }, "Update from Generative AI");
}

export function updateImages(
  imagesFields: FieldToComplete[],
  unsplashResults: UnsplashImage[],
  fillEmptyOnly: boolean
) {
  imagesFields
    .filter(i => {
      if (fillEmptyOnly) {
        const maybeNode = findNode(documentStore.document.tree, i.nodeId);
        if (!maybeNode) return false;
        const currentValue = lodash.get(
          maybeNode.data.properties,
          i.controlPath
        );

        return (
          currentValue === null ||
          currentValue === undefined ||
          currentValue === ""
        );
      }

      return true;
    })
    .forEach((image, index) => {
      const img = unsplashResults[index];

      documentStore.unthrottledPropertyChanged({
        elementId: image.nodeId,
        path: image.controlPath,
        value: {
          id: -1,
          type: "external_image",
          url: img.urls.regular,
          alt: img.alt_description || "",
          caption: img.description || ""
        },
        meta: {
          snapshotLabel: "Update image from Generative AI"
        }
      });
    });
}

// TODO: Refactor to a functional style

export function getFieldsToComplete(
  node: TreeNode,
  acc: FieldToComplete[] = [],
  parentPath: string = ""
): FieldToComplete[] {
  const element = configStore.element(node.data.type);

  if (element.settings && element.settings.disableAI) return acc;
  if (ELEMENTS_TO_IGNORE.includes(node.data.type) && isFutureLayerMode()) return acc;

  let acc_: FieldToComplete[] = [
    ...acc,
    ...getFieldsToCompleteFromControls(
      element.controls.contentSections,
      "content",
      node,
      [],
      parentPath
    )
  ];

  const nodeDescriptiveType = node.data?.properties?.settings?.futurelayer?.more_descriptive_type;
  const childPath = nodeDescriptiveType ? 
    (parentPath ? `${parentPath} > ${nodeDescriptiveType}` : nodeDescriptiveType) : 
    parentPath;

  node.children.forEach(n => {
    acc_ = getFieldsToComplete(n, acc_, childPath);
  });

  return acc_;
}

export function getImagesToComplete(
  node: TreeNode,
  acc: FieldToComplete[] = [],
  parentPath: string = ""
): FieldToComplete[] {
  const element = configStore.element(node.data.type);

  if (element.settings && element.settings.disableAI) return acc;
  if (ELEMENTS_TO_IGNORE.includes(node.data.type) && isFutureLayerMode()) return acc;

  let acc_: FieldToComplete[] = [
    ...acc,
    ...getImagesToCompleteFromControls(
      configStore.element(node.data.type).controls.contentSections,
      "content",
      node,
      [],
      parentPath
    ),
    ...getBackgroundImagesToComplete(node)
  ];

  const nodeDescriptiveType = node.data?.properties?.settings?.futurelayer?.more_descriptive_type;
  const childPath = nodeDescriptiveType ? 
    (parentPath ? `${parentPath} > ${nodeDescriptiveType}` : nodeDescriptiveType) : 
    parentPath;

  node.children.forEach(n => {
    acc_ = getImagesToComplete(n, acc_, childPath);
  });

  return acc_;
}

export function getBackgroundImagesToComplete(
  node: TreeNode
): FieldToComplete[] {
  const backgroundImagesToComplete: FieldToComplete[] = [];

  // hard-coded section background image support
  if (node.data.type === SECTION_SLUG) {
    const fieldToComplete = {
      nodeId: node.id,
      nodeName: configStore.element(node.data.type).name,
      controlPath: "design.background.image",
      field: {
        description: "Section - Background Image",
        label: "Background Image",
        value: "",
        type: "wpmedia",
        locked: false
      }
    };

    // ignore empty images
    if (lodash.get(node.data.properties, "design.background.image")) {
      backgroundImagesToComplete.push(fieldToComplete);
    }
  }

  return backgroundImagesToComplete;
}

function getImagesToCompleteFromControls(
  controls: Control[],
  path: string,
  node: TreeNode,
  acc: FieldToComplete[] = [],
  parentPath: string = ""
) {
  const element = configStore.element(node.data.type);
  const _acc = acc;

  controls.forEach(control => {
    const controlPath = `${path}.${control.slug}`;

    if (control.options.type === "wpmedia") {
      _acc.push({
        nodeId: node.id,
        nodeName: element.name,
        controlPath: controlPath,
        field: {
          description: getFieldDescription(
            control,
            controlPath,
            node.data.type,
            node,
            parentPath
          ),
          label: control.label,
          value: lodash.get(node.data.properties, controlPath),
          type: control.options.type,
          locked: isFieldLocked(node, controlPath)
        }
      });
    }

    getImagesToCompleteFromControls(control.children, controlPath, node, _acc, parentPath);
  });

  return _acc;
}

function getFieldsToCompleteFromControls(
  controls: Control[],
  path: string,
  node: TreeNode,
  acc: FieldToComplete[] = [],
  parentPath: string = ""
) {
  const element = configStore.element(node.data.type);
  const _acc = acc;

  controls.forEach(control => {
    const controlPath = `${path}.${control.slug}`;

    if (
      isControlSupported(control) &&
      (!isValueEmpty(control, path, node) || control.options.type === "link")
    ) {
      _acc.push({
        nodeId: node.id,
        controlPath: controlPath,
        nodeName: element.name,
        field: {
          description: getFieldDescription(
            control,
            controlPath,
            node.data.type,
            node,
            parentPath
          ),
          label: control.label,
          value: lodash.get(node.data.properties, controlPath),
          type: control.options.type,
          locked: isFieldLocked(node, controlPath)
        }
      });
    }

    // Ignore controls inside repeaters
    if (control.options.type === "repeater") return;

    getFieldsToCompleteFromControls(control.children, controlPath, node, _acc, parentPath);
  });

  return _acc;
}

function isValueEmpty(control: Control, path: string, node: TreeNode) {
  const controlPath = `${path}.${control.slug}`;
  const value = lodash.get(node.data.properties, controlPath, "");
  return value === "" || value === null || value === undefined;
}

function isControlSupported(control: Control) {
  return ["text", "richtext", "repeater", "link"].includes(
    control.options.type
  );
}

function getFieldDescription(
  control: Control,
  controlPath: string,
  elementType: string,
  node: TreeNode,
  parentPath: string = ""
) {
  const nodeDescriptiveType = node.data?.properties?.settings?.futurelayer?.more_descriptive_type;
  const elementName = nodeDescriptiveType || configStore.element(elementType).name;
  
  const prefix = parentPath ? `${parentPath} > ${elementName}` : elementName;

  if (control.options.type === "repeater") {
    const childControls = control.children
      .filter(isControlSupported)
      .map(childControl => childControl.slug);

    const keys = childControls.join(", ");
    return `${prefix} - An array of objects with the following keys: ${keys}.`;
  }

  if (controlPath.endsWith("button.text")) {
    return `${prefix} - Button`;
  }

  if (elementName === control.label) {
    return prefix;
  }

  if (control.options.type === "link") {
    return `${prefix} - URL`;
  }

  return `${prefix} - ${control.label}`;
}

export function generateElementDescription(
  tree: Tree,
  node: TreeNode,
  postTitle: string,
  postType: string
) {
  const elementName = configStore.element(node.data.type).name;
  const nodeIndex = getNodeIndex(tree, node.id) as number;
  const parent = getOutermostAncestor(tree, node.id);
  const parentName = parent ? configStore.element(parent.data.type).name : "";
  const parts = [];

  if (parent) {
    const parentIndex = getNodeIndex(tree, parent.id) as number;
    const ordinal = numberToOrdinal(parentIndex + 1);

    parts.push(elementName);
    parts.push("on");
    parts.push(ordinal);
    parts.push(parentName);
  } else if (node.data.type === SECTION_SLUG) {
    const ordinal = numberToOrdinal(nodeIndex + 1);
    parts.push(nodeIndex === 0 ? "Hero" : ordinal);
    parts.push("section");
  }

  parts.push("on");
  parts.push(`"${postTitle}"`);

  parts.push(postType.replace("breakdance_", ""));

  return parts.join(" ");
}

export function getElementFriendlyName(node: TreeNode) {
  const friendlyName = node.data?.properties?.meta?.friendlyName;
  if (friendlyName) return friendlyName;
  const elementName = configStore.element(node.data.type).name;
  return `${elementName} #${node.id}`;
}

export function getElementPrompt(
  node: TreeNode
): Partial<PromptInfo> | undefined {
  return node.data?.properties?.meta?.promptInfo;
}

export function getElementDescription(node: TreeNode) {
  const doc = documentStore.document.documentMeta;
  const metadata = getElementPrompt(node);

  // Get description saved in element metadata
  if (metadata?.sectionDescription) {
    return metadata.sectionDescription;
  }

  // Otherwise, generate a default description
  return generateElementDescription(
    documentStore.document.tree,
    node,
    doc.titleLabel,
    doc.postType
  );
}

export function getElementQualifier(node: TreeNode) {
  const metadata = getElementPrompt(node);
  return metadata?.qualifiers || "";
}

export function getElementTemperature(node: TreeNode) {
  const temperature = getElementPrompt(node)?.temperature;
  return temperature !== undefined ? temperature : 1;
}

export function saveToElementMetadata(node: TreeNode, promptInfo: PromptInfo) {
  const elementName = configStore.element(node.data.type).name;

  documentStore.unthrottledPropertyChanged({
    elementId: node.id,
    path: "meta.promptInfo",
    meta: {
      snapshotLabel: `Updated prompt info for element "${elementName}"`
    },
    value: promptInfo
  });
}

export function lockFieldFromAI(node: TreeNode, fieldPath: string) {
  const elementName = configStore.element(node.data.type).name;

  const lockedFields = getElementPrompt(node)?.lockedFields || [];
  const newLockedFields = defensiveCopy(lockedFields);

  if (!newLockedFields.includes(fieldPath)) {
    newLockedFields.push(fieldPath);
  }

  documentStore.unthrottledPropertyChanged({
    elementId: node.id,
    path: `meta.promptInfo.lockedFields`,
    meta: {
      snapshotLabel: `Locked field "${fieldPath}" for element "${elementName}"`
    },
    value: newLockedFields
  });
}

export function unlockFieldFromAI(node: TreeNode, fieldPath: string) {
  const elementName = configStore.element(node.data.type).name;

  const lockedFields = getElementPrompt(node)?.lockedFields || [];
  const newLockedFields = defensiveCopy(lockedFields);

  const index = newLockedFields.indexOf(fieldPath);
  if (index > -1) {
    newLockedFields.splice(index, 1);
  }

  documentStore.unthrottledPropertyChanged({
    elementId: node.id,
    path: `meta.promptInfo.lockedFields`,
    meta: {
      snapshotLabel: `Unlocked field "${fieldPath}" for element "${elementName}"`
    },
    value: newLockedFields
  });
}

export function toggleFieldLock(node: TreeNode, fieldPath: string) {
  if (isFieldLocked(node, fieldPath)) {
    unlockFieldFromAI(node, fieldPath);
  } else {
    lockFieldFromAI(node, fieldPath);
  }
}

export function isFieldLocked(node: TreeNode, fieldPath: string) {
  const lockedFields = getElementPrompt(node)?.lockedFields || [];
  return lockedFields.includes(fieldPath);
}

export function getLockedFieldsPath(node: TreeNode) {
  return getElementPrompt(node)?.lockedFields || [];
}

export function extractExistingContentFromSection(node: TreeNode): string {
  const fields = getFieldsToComplete(node);

  if (!fields.length) {
    return "";
  }

  const contentMap = fields.map((field) => {
    const value = field.field.value;

    if (value === null || value === undefined || value === "") {
      return null;
    }

    let formattedValue = value;
    if (Array.isArray(value) || typeof value === "object") {
      formattedValue = jsonStringifyOrUndefined(value) || String(value);
    }

    return `${field.field.description}: ${formattedValue}`;
  }).filter(Boolean);

  if (!contentMap.length) {
    return "";
  }

  return `Old section content:\n${contentMap.join("\n")}`;
}

export function handleAiError(error: string) {
  const defaultErrorMessage =
    "Failed to get a response from OpenAI API, please try again.";

  const cleanErrorString = error
    .slice(1, -1) // Remove first and last character (")
    .replace(/\\n/g, "") // Remove new lines
    .replace(/\\"/g, '"') // Replace \" with "
    .trim();

  const response = pipe(
    fpJsonParseTypesafe(cleanErrorString, OpenAIErrorV),
    fold(
      () => null,
      res => res
    )
  );

  const message = response?.error?.message || defaultErrorMessage;

  console.log(cleanErrorString);
  NotificationLogger.log.errorMessage(message);
}
